import { useState } from "react";
import {
  selectAvailableRoleNames,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isInternalRole } from "@/lib/roles";

const ALL = "all";

const Field = ({ id, label, value, onChange, options }) => (
  <div className="grid gap-2">
    <Label htmlFor={id}>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} className="w-full" data-testid={`${id}_select`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map(option => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

/** Mute everyone else at once, or ask them to unmute (interviewer only). */
export const MuteAllDialog = ({ open, onOpenChange }) => {
  const hmsActions = useHMSActions();
  const roles = (useHMSStore(selectAvailableRoleNames) || []).filter(
    role => !isInternalRole(role)
  );
  const [role, setRole] = useState(ALL);
  const [type, setType] = useState(ALL);
  const [source, setSource] = useState(ALL);
  const [action, setAction] = useState("mute");
  const [applying, setApplying] = useState(false);

  const apply = async () => {
    setApplying(true);
    try {
      await hmsActions.setRemoteTracksEnabled({
        enabled: action === "unmute",
        type: type === ALL ? undefined : type,
        source: source === ALL ? undefined : source,
        roles: role === ALL ? undefined : [role],
      });
      onOpenChange(false);
    } catch (error) {
      toast.error("That didn't work", { description: error?.message });
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Mute/Unmute Remote Tracks</DialogTitle>
          <DialogDescription>
            Applies to everyone else in the call.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <Field
            id="mute_action"
            label="Action"
            value={action}
            onChange={setAction}
            options={[
              { value: "mute", label: "Mute" },
              { value: "unmute", label: "Ask to unmute" },
            ]}
          />
          <Field
            id="mute_role"
            label="Role"
            value={role}
            onChange={setRole}
            options={[
              { value: ALL, label: "All roles" },
              ...roles.map(name => ({ value: name, label: name })),
            ]}
          />
          <Field
            id="mute_type"
            label="Track type"
            value={type}
            onChange={setType}
            options={[
              { value: ALL, label: "Audio and video" },
              { value: "audio", label: "Audio" },
              { value: "video", label: "Video" },
            ]}
          />
          <Field
            id="mute_source"
            label="Source"
            value={source}
            onChange={setSource}
            options={[
              { value: ALL, label: "Camera, microphone and screen" },
              { value: "regular", label: "Camera and microphone" },
              { value: "screen", label: "Screen share" },
            ]}
          />
        </div>
        <DialogFooter>
          <Button
            onClick={apply}
            disabled={applying}
            data-testid="mute_all_apply_btn"
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
